import type {
  ConceptGroupSelection,
  ConceptGroupsConfidence,
  LegacyConceptGroup,
} from '@verantos-dev/types-concepts';

import type { CohortDefinitionStoreActions, CohortDefinitionStoreState } from '../types';

type Store = CohortDefinitionStoreState & CohortDefinitionStoreActions;
type SetState = (
  partial: Store | Partial<Store> | ((state: Store) => Store | Partial<Store>)
) => void;

export const createConceptGroupSlice = (set: SetState) => ({
  conceptGroups: undefined as LegacyConceptGroup[] | undefined,
  conceptGroupSelections: [] as ConceptGroupSelection[],
  conceptGroupsConfidence: undefined as ConceptGroupsConfidence | undefined,
  recentlyAppliedConceptGroups: [] as Array<{
    avId: string;
    conceptGroupName: string;
    appliedAt: number;
  }>,
  pendingPreviewAvIds: [] as string[],

  setConceptGroups: (groups: LegacyConceptGroup[]) => set({ conceptGroups: groups }),

  setConceptGroupSelections: (selections: ConceptGroupSelection[]) =>
    set((state) => {
      // Preserve local `reported=true` when the user has dismissed a chip in
      // this session — backend responses re-broadcast prior-turn selections
      // (post OR-5840) with their backend-side `reported=false` for AVs the
      // user already manually fixed (OR-5948 AC #2). Without this guard, the
      // chip would reappear after every subsequent RA message even though the
      // error condition was already resolved locally.
      // TODO: cleanup with gate_concept_group_selection_on_mutations ff
      if (!state.conceptGroupSelections.length) {
        return { conceptGroupSelections: selections };
      }
      const localReportedKeys = new Set<string>();
      for (const prior of state.conceptGroupSelections) {
        if (prior.reported) {
          const key = prior.anchorId ?? prior.filterKey;
          if (key) localReportedKeys.add(key);
        }
      }
      if (localReportedKeys.size === 0) {
        return { conceptGroupSelections: selections };
      }
      const merged = selections.map((sel) => {
        const key = sel.anchorId ?? sel.filterKey;
        if (key && localReportedKeys.has(key) && !sel.reported) {
          return { ...sel, reported: true };
        }
        return sel;
      });
      return { conceptGroupSelections: merged };
    }),

  markConceptGroupSelectionsAsReported: ({
    filterKeys,
    containerName,
    markAll,
    statuses,
  }: {
    filterKeys?: string[];
    containerName?: string;
    markAll?: boolean;
    statuses?: ReadonlyArray<ConceptGroupSelection['status']>;
  }) =>
    set((state) => {
      const selections = state.conceptGroupSelections;
      if (!selections.length) return state;

      const statusAllowed = (s: ConceptGroupSelection) =>
        !statuses || statuses.length === 0 || statuses.includes(s.status);

      let matcher: (s: ConceptGroupSelection) => boolean;
      if (filterKeys?.length) {
        matcher = (s) => filterKeys.includes(s.filterKey) && statusAllowed(s);
      } else if (markAll) {
        matcher = (s) => {
          const isErrorWithoutGroups =
            s.status === 'error' &&
            (!s.selectedConceptGroups || s.selectedConceptGroups.length === 0);
          return !isErrorWithoutGroups && statusAllowed(s);
        };
      } else if (containerName) {
        const normalized = containerName.toLowerCase();
        matcher = (s) => s.containerName?.toLowerCase() === normalized && statusAllowed(s);
      } else {
        return state;
      }

      // Skip allocation when nothing would change (selection already reported).
      const willMutate = selections.some((s) => matcher(s) && !s.reported);
      if (!willMutate) return state;

      return {
        conceptGroupSelections: selections.map((s) =>
          matcher(s) && !s.reported ? { ...s, reported: true } : s
        ),
      };
    }),

  setConceptGroupsConfidence: (confidence: ConceptGroupsConfidence) =>
    set({ conceptGroupsConfidence: confidence }),

  clearConceptGroupsConfidence: () => set({ conceptGroupsConfidence: undefined }),

  markConceptGroupAsGenerated: (conceptGroupName: string) =>
    set((state) => ({
      conceptGroupsConfidence: state.conceptGroupsConfidence?.map((item) => ({
        ...item,
        conceptGroups: item.conceptGroups?.map((cg) =>
          cg.span.toLowerCase().trim() === conceptGroupName.toLowerCase().trim()
            ? { ...cg, generated: true }
            : cg
        ),
      })),
    })),

  addRecentlyAppliedConceptGroup: (avId: string, conceptGroupName: string) =>
    set((state) => ({
      recentlyAppliedConceptGroups: [
        ...state.recentlyAppliedConceptGroups,
        { avId, conceptGroupName, appliedAt: Date.now() },
      ],
    })),

  clearRecentlyAppliedConceptGroup: (avId: string, conceptGroupName: string) =>
    set((state) => ({
      recentlyAppliedConceptGroups: state.recentlyAppliedConceptGroups.filter(
        (entry) => !(entry.avId === avId && entry.conceptGroupName === conceptGroupName)
      ),
    })),

  clearAllRecentlyApplied: () => set({ recentlyAppliedConceptGroups: [] }),

  setPendingPreviewAvIds: (avIds: string[]) => set({ pendingPreviewAvIds: avIds }),

  shiftPendingPreviewAvId: () =>
    set((state) => ({ pendingPreviewAvIds: state.pendingPreviewAvIds.slice(1) })),
});
